/**
 * Workflow API
 */

import apiClient from './client';

export interface WorkflowNode {
  id: string;
  type: string;
  position: { x: number; y: number };
  data: Record<string, unknown>;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
}

export interface ChatbotTheme {
  primaryColor?: string;
  backgroundColor?: string;
  textColor?: string;
  botName?: string;
  welcomeMessage?: string;
  quickPrompts?: string[];
  // 사용자 질문 스타일
  userBubbleColor?: string;
  userTextColor?: string;
  // 챗봇 답변 스타일
  botBubbleColor?: string;
  botTextColor?: string;
  // 챗봇 아이콘 스타일
  botIconStyle?: 'white' | 'black';
}

export interface Workflow {
  id: string;
  name: string;
  description?: string;
  status: 'draft' | 'deployed' | 'archived';
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  knowledgeBaseIds?: string[];
  owner?: string;
  ownerName?: string;
  isAdminOwned?: boolean;
  tenantId?: string;   // 소속 테넌트 ID
  customerId?: string; // 소속 고객사 ID
  createdAt: string;
  updatedAt: string;
  deployedAt?: string;
  isSharedAsTemplate?: boolean;
  isSharedAsChatbot?: boolean;
  teamId?: string;  // 레거시 (구 단일 팀, 하위 호환 읽기용)
  sharedTeamIds?: string[];  // 레거시 (이전 버전 하위호환)
  sharedChatbotTeamIds?: string[];  // 챗봇 공유 팀 ID 목록
  sharedTemplateTeamIds?: string[];  // 템플릿 공유 팀 ID 목록
  theme?: ChatbotTheme;
}

export interface CreateWorkflowRequest {
  name: string;
  description?: string;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
  knowledgeBaseIds?: string[];
  theme?: ChatbotTheme;
}

// ── 실행 로그 타입 ────────────────────────────────────────────────────────────

export type WorkflowRunStatus = 'success' | 'failed' | 'skipped';

export interface NodeRunResult {
  nodeId: string;
  nodeType: string;
  status: WorkflowRunStatus;
  message?: string;
  durationMs?: number;
}

export interface WorkflowRunLog {
  logId: string;
  workflowId: string;
  workflowName: string;
  status: WorkflowRunStatus;
  triggeredBy: 'schedule' | 'manual';
  startedAt: string;
  finishedAt: string;
  durationMs: number;
  nodeResults: NodeRunResult[];
  errorMessage?: string;
  metadata?: {
    retrievalQuery?: string;
    executedSql?: string;
    rowCount?: number;
    retrievalSummary?: string;
    retrievalRows?: Record<string, unknown>[];
    scheduleLabel?: string;
    tenantId?: string;
    customerId?: string;
  };
}

export interface UpdateWorkflowRequest {
  name?: string;
  description?: string;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
  knowledgeBaseIds?: string[];
  status?: 'draft' | 'deployed' | 'archived';
  isSharedAsTemplate?: boolean;
  isSharedAsChatbot?: boolean;
  sharedTeamIds?: string[];  // 레거시
  sharedChatbotTeamIds?: string[];  // 챗봇 공유 팀 ID 목록
  sharedTemplateTeamIds?: string[];  // 템플릿 공유 팀 ID 목록
  theme?: ChatbotTheme;
}

// 워크플로우 변경 이벤트 발생 (사이드바 등에서 사용)
const emitWorkflowChanged = () => {
  window.dispatchEvent(new CustomEvent('workflow-changed'));
};

export const workflowApi = {
  async list(options?: { deployed?: boolean; limit?: number }) {
    const params = new URLSearchParams();
    params.append('limit', String(options?.limit ?? 100));
    if (options?.deployed) {
      params.append('deployed', 'true');
    }
    const url = `/api/workflows?${params.toString()}`;
    const response = await apiClient.get<{ success: boolean; data: Workflow[] }>(url);
    return response.data.data;
  },

  async get(id: string) {
    const response = await apiClient.get<{ success: boolean; data: Workflow }>(`/api/workflows/${id}`);
    return response.data.data;
  },

  async create(data: CreateWorkflowRequest) {
    const response = await apiClient.post<{ success: boolean; data: Workflow }>('/api/workflows', data);
    emitWorkflowChanged();
    return response.data.data;
  },

  async update(id: string, data: UpdateWorkflowRequest) {
    const response = await apiClient.put<{ success: boolean; data: Workflow }>(`/api/workflows/${id}`, data);
    emitWorkflowChanged();
    return response.data.data;
  },

  async delete(id: string) {
    await apiClient.delete(`/api/workflows/${id}`);
    emitWorkflowChanged();
  },

  async deploy(id: string) {
    const response = await apiClient.post<{ success: boolean; data: { workflowId: string; deployedAt: string; endpoint: string } }>(
      `/api/workflows/${id}/deploy`
    );
    emitWorkflowChanged();
    return response.data.data;
  },

  async undeploy(id: string) {
    const response = await apiClient.post<{ success: boolean; data: { id: string; status: string } }>(
      `/api/workflows/${id}/undeploy`
    );
    emitWorkflowChanged();
    return response.data.data;
  },

  async validate(id: string) {
    const response = await apiClient.post<{ success: boolean; data: { isValid: boolean; errors: string[] } }>(
      `/api/workflows/${id}/validate`
    );
    return response.data.data;
  },

  async getLogs(id: string, limit = 20): Promise<WorkflowRunLog[]> {
    const response = await apiClient.get<{ success: boolean; data: WorkflowRunLog[] }>(
      `/api/workflows/${id}/logs?limit=${limit}`
    );
    return response.data.data;
  },

  async getActiveSchedules(): Promise<string[]> {
    const response = await apiClient.get<{ success: boolean; data: string[] }>(
      '/api/workflows/schedules/active'
    );
    return response.data.data;
  },

  async pauseSchedule(id: string): Promise<void> {
    await apiClient.post(`/api/workflows/${id}/schedule/pause`);
  },

  async resumeSchedule(id: string): Promise<void> {
    await apiClient.post(`/api/workflows/${id}/schedule/resume`);
  },

  async run(id: string): Promise<{ status: string; errorMessage?: string; nodeResults: Array<{ nodeType: string; status: string; message?: string; durationMs?: number }>; durationMs?: number }> {
    const response = await apiClient.post<{ success: boolean; data: { status: string; errorMessage?: string; nodeResults: Array<{ nodeType: string; status: string; message?: string; durationMs?: number }>; durationMs?: number } }>(
      `/api/workflows/${id}/run`
    );
    return response.data.data;
  },
};

export default workflowApi;
